import * as Sentry from "@sentry/react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import type { SearchResult } from "./utils";
import { createSearchRegex } from "./utils";

/**
 * A searchable source that can be searched, replaced, and navigated
 */
export interface Searchable {
  /** Unique identifier */
  id: string;
  /** Display name (e.g., "Document", "Story Bible") */
  name: string;
  /** Search for term, return results */
  search: (term: string) => SearchResult[];
  /** Search using a pre-created regex (avoids redundant regex creation across searchables) */
  searchWithRegex?: (regex: RegExp) => SearchResult[];
  /** Replace a single result */
  replace: (result: SearchResult, replacement: string) => void;
  /** Replace all occurrences */
  replaceAll: (term: string, replacement: string) => void;
  /** Scroll to and highlight a result without acquiring editing focus. */
  scrollToResult: (result: SearchResult) => void;
  /** Get the DOM element for this searchable (used for DOM order sorting) */
  getElement: () => HTMLElement | null;
}

/**
 * Current search state for broadcasting to searchables
 */
export interface SearchState {
  /** Current search term */
  searchTerm: string;
  /** Currently selected/focused result */
  selectedResult: SearchResult | null;
}

/**
 * Stable registry value - callbacks that never change identity.
 * Use useSearchableRegistry() for consumers that don't need React re-renders on searchState changes.
 */
export interface SearchableRegistryValue {
  register: (searchable: Searchable) => void;
  unregister: (id: string) => void;
  searchAll: (term: string) => SearchResult[];
  replaceResult: (result: SearchResult, replacement: string) => void;
  replaceAllGlobal: (term: string, replacement: string) => void;
  scrollToResult: (result: SearchResult) => void;
  getSearchables: () => Searchable[];
  updateSearchState: (term: string, selectedResult?: SearchResult | null) => void;
  onOpenFindReplace: (callback: (() => void) | null) => () => void;
  requestOpenFindReplace: () => void;
  /** Subscribe to search state changes without causing React re-renders */
  subscribeToSearchState: (callback: (state: SearchState) => void) => () => void;
  /** Get current search state synchronously (no re-render) */
  getSearchState: () => SearchState;
}

/**
 * Full context value including searchState.
 * Components using useSearchableContext() will re-render on searchState changes.
 */
export interface SearchableContextValue extends SearchableRegistryValue {
  searchState: SearchState;
}

// Stable registry context - value never changes after mount
const SearchableRegistryContext = createContext<SearchableRegistryValue | null>(null);
// Registry version context - only consumers that need registration churn should subscribe
const SearchableVersionContext = createContext(0);
// Dynamic search state context - value changes when searchState updates
const SearchStateContext = createContext<SearchState>({ searchTerm: "", selectedResult: null });

interface SearchableProviderProps {
  children: ReactNode;
}

// No-op registry for components outside the provider
const NO_OP_REGISTRY: SearchableRegistryValue = {
  register: () => {},
  unregister: () => {},
  searchAll: () => [],
  replaceResult: () => {},
  replaceAllGlobal: () => {},
  scrollToResult: () => {},
  getSearchables: () => [],
  updateSearchState: () => {},
  onOpenFindReplace: () => () => {},
  requestOpenFindReplace: () => {},
  subscribeToSearchState: () => () => {},
  getSearchState: () => ({ searchTerm: "", selectedResult: null }),
};

const NO_OP_SEARCH_STATE: SearchState = { searchTerm: "", selectedResult: null };

export function SearchableProvider({ children }: SearchableProviderProps): JSX.Element {
  const searchablesRef = useRef<Map<string, Searchable>>(new Map());
  const openFindReplaceCallbackRef = useRef<(() => void) | null>(null);

  // Search state: ref for synchronous access + React state for re-rendering consumers
  const searchStateRef = useRef<SearchState>({ searchTerm: "", selectedResult: null });
  const searchStateSubscribersRef = useRef<Set<(state: SearchState) => void>>(new Set());
  const [searchState, setSearchState] = useState<SearchState>({ searchTerm: "", selectedResult: null });

  // Version counter that bumps when searchables register/unregister,
  // allowing consumers to react to changes in the set of registered searchables
  const [searchableVersion, setSearchableVersion] = useState(0);

  const register = useCallback((searchable: Searchable) => {
    searchablesRef.current.set(searchable.id, searchable);
    setSearchableVersion(v => v + 1);
  }, []);

  const unregister = useCallback((id: string) => {
    searchablesRef.current.delete(id);
    setSearchableVersion(v => v + 1);
  }, []);

  const searchAll = useCallback((term: string): SearchResult[] => {
    if (!term) return [];

    // PERF: Create regex once, share across all searchables (avoids 259 redundant RegExp creations)
    const regex = createSearchRegex(term);
    if (!regex) return [];

    // Collect results with their source elements for sorting
    const resultsWithElements: Array<{ result: SearchResult; element: HTMLElement | null }> = [];

    for (const searchable of searchablesRef.current.values()) {
      try {
        // PERF: Use searchWithRegex (shared regex + cached text segments) when available
        const results = searchable.searchWithRegex
          ? searchable.searchWithRegex(regex)
          : searchable.search(term);
        const element = searchable.getElement();
        for (const result of results) {
          resultsWithElements.push({ result, element });
        }
      } catch (e) {
        Sentry.captureException(e, { tags: { feature: "global_search", field: searchable.name } });
      }
    }

    // Sort by DOM position (like Chrome's native Ctrl+F)
    // This ensures results appear in visual order: top-to-bottom, left-to-right
    resultsWithElements.sort((a, b) => {
      const elA = a.element;
      const elB = b.element;

      // Document always comes first (it's the main editor at the top)
      const aIsDocument = a.result.sourceId === "document";
      const bIsDocument = b.result.sourceId === "document";
      if (aIsDocument && !bIsDocument) return -1;
      if (!aIsDocument && bIsDocument) return 1;

      // If same source, sort by position within that source
      if (a.result.sourceId === b.result.sourceId) {
        return a.result.from - b.result.from;
      }

      // If either element is missing, keep stable order by sourceId
      if (!elA || !elB) {
        return a.result.sourceId.localeCompare(b.result.sourceId);
      }

      // Use compareDocumentPosition for true DOM order
      const position = elA.compareDocumentPosition(elB);

      // Handle disconnected elements (different documents) - use sourceId for stability
      if (position & Node.DOCUMENT_POSITION_DISCONNECTED) {
        return a.result.sourceId.localeCompare(b.result.sourceId);
      }

      if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
        // elB follows elA in DOM order, so elA comes first
        return -1;
      } else if (position & Node.DOCUMENT_POSITION_PRECEDING) {
        // elB precedes elA in DOM order, so elB comes first
        return 1;
      }

      // Same element or contained - sort by position within
      return a.result.from - b.result.from;
    });

    return resultsWithElements.map(({ result }) => result);
  }, []);

  const replaceResult = useCallback((result: SearchResult, replacement: string) => {
    const searchable = searchablesRef.current.get(result.sourceId);
    if (searchable) {
      searchable.replace(result, replacement);
    }
  }, []);

  const replaceAllGlobal = useCallback((term: string, replacement: string) => {
    for (const searchable of searchablesRef.current.values()) {
      try {
        searchable.replaceAll(term, replacement);
      } catch (e) {
        Sentry.captureException(e, { tags: { feature: "global_replace", field: searchable.name } });
      }
    }
  }, []);

  const scrollToResult = useCallback((result: SearchResult) => {
    const searchable = searchablesRef.current.get(result.sourceId);
    if (searchable) {
      searchable.scrollToResult(result);
    }
  }, []);

  const getSearchables = useCallback((): Searchable[] => {
    return Array.from(searchablesRef.current.values());
  }, []);

  // Subscribe to search state changes (imperative, no React re-renders)
  const subscribeToSearchState = useCallback((callback: (state: SearchState) => void): (() => void) => {
    searchStateSubscribersRef.current.add(callback);
    return () => {
      searchStateSubscribersRef.current.delete(callback);
    };
  }, []);

  // Get current search state synchronously
  const getSearchState = useCallback((): SearchState => {
    return searchStateRef.current;
  }, []);

  // Update search state - notifies subscribers imperatively, then updates React state
  const updateSearchState = useCallback((term: string, selectedResult: SearchResult | null = null) => {
    const prev = searchStateRef.current;
    // PERF: Skip if nothing changed
    if (
      prev.searchTerm === term &&
      prev.selectedResult?.sourceId === selectedResult?.sourceId &&
      prev.selectedResult?.from === selectedResult?.from &&
      prev.selectedResult?.to === selectedResult?.to
    ) {
      return;
    }
    const next: SearchState = { searchTerm: term, selectedResult };
    searchStateRef.current = next;

    // Notify subscribers (field components that update decorations imperatively)
    for (const callback of searchStateSubscribersRef.current) {
      try {
        callback(next);
      } catch (e) {
        console.error("[SearchableContext] subscriber error:", e);
      }
    }

    // Update React state (for components that need re-rendering, e.g. FindAndReplace UI)
    setSearchState(next);
  }, []);

  // Register callback to open find/replace (called by TiptapDocumentSB)
  const onOpenFindReplace = useCallback((callback: (() => void) | null): (() => void) => {
    openFindReplaceCallbackRef.current = callback;
    return () => {
      openFindReplaceCallbackRef.current = null;
    };
  }, []);

  // Request to open find/replace (called by searchable fields on Ctrl+F)
  const requestOpenFindReplace = useCallback(() => {
    if (openFindReplaceCallbackRef.current) {
      openFindReplaceCallbackRef.current();
    }
  }, []);

  // Registry value stays stable so useSearchableRegistry consumers do not re-render
  // when fields register/unregister. Consumers that need registration churn use
  // useSearchableVersion().
  const registryValue = useMemo<SearchableRegistryValue>(() => ({
    register,
    unregister,
    searchAll,
    replaceResult,
    replaceAllGlobal,
    scrollToResult,
    getSearchables,
    updateSearchState,
    onOpenFindReplace,
    requestOpenFindReplace,
    subscribeToSearchState,
    getSearchState,
  }), [register, unregister, searchAll, replaceResult, replaceAllGlobal, scrollToResult, getSearchables, updateSearchState, onOpenFindReplace, requestOpenFindReplace, subscribeToSearchState, getSearchState]);

  return (
    <SearchableRegistryContext.Provider value={registryValue}>
      <SearchableVersionContext.Provider value={searchableVersion}>
        <SearchStateContext.Provider value={searchState}>
          {children}
        </SearchStateContext.Provider>
      </SearchableVersionContext.Provider>
    </SearchableRegistryContext.Provider>
  );
}

/**
 * Hook to access the full searchable context (backward compatible).
 * Components using this will re-render when searchState changes.
 * For consumers that don't need searchState re-renders, use useSearchableRegistry() instead.
 */
export function useSearchableContext(): SearchableContextValue {
  const registry = useContext(SearchableRegistryContext);
  const searchState = useContext(SearchStateContext);
  if (!registry) {
    return { ...NO_OP_REGISTRY, searchState: NO_OP_SEARCH_STATE };
  }
  return { ...registry, searchState };
}

/**
 * Hook to access only the stable registry (no re-renders on searchState changes).
 * Use this for consumers that handle searchState changes imperatively via subscribeToSearchState.
 */
export function useSearchableRegistry(): SearchableRegistryValue {
  const registry = useContext(SearchableRegistryContext);
  if (!registry) {
    return NO_OP_REGISTRY;
  }
  return registry;
}

/**
 * Hook to react to registry membership changes.
 * Keep this separate from useSearchableRegistry so field components can use
 * stable registry callbacks without re-rendering on every register/unregister.
 */
export function useSearchableVersion(): number {
  return useContext(SearchableVersionContext);
}

/**
 * Hook to access only the search state (re-renders on changes).
 * Use this for components that need to render based on searchState.
 */
export function useSearchState(): SearchState {
  return useContext(SearchStateContext);
}

/**
 * Hook to handle Ctrl/Cmd+F in searchable fields to open global find/replace
 * @param elementRef - Ref to the element to attach the keydown listener to
 */
export function useSearchableKeyboardShortcut(elementRef: RefObject<HTMLElement | null>): void {
  const { requestOpenFindReplace } = useSearchableRegistry();

  useEffect(() => {
    const element = elementRef?.current;
    if (!element) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "f" && (e.metaKey || e.ctrlKey) && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        requestOpenFindReplace();
      }
    };

    element.addEventListener("keydown", handleKeyDown);
    return () => element.removeEventListener("keydown", handleKeyDown);
  }, [elementRef, requestOpenFindReplace]);
}

export default SearchableRegistryContext;
